import type { ClaimCard, ReviewResultOut } from '../types';

type CsvCell = string | number | boolean | null | undefined;

export function escapeCsvCell(value: CsvCell): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function rowsToCsv(headers: string[], rows: CsvCell[][]): string {
  const lines = [
    headers.map(escapeCsvCell).join(','),
    ...rows.map((row) => row.map(escapeCsvCell).join(',')),
  ];
  return `${lines.join('\r\n')}\r\n`;
}

export function downloadTextFile(
  filename: string,
  content: string,
  mimeType = 'text/csv;charset=utf-8',
): void {
  const blob = new Blob([content], { type: mimeType });
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
  }
}

function timestampForFilename(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
}

function sanitizeFilenameSegment(name: string): string {
  const sanitized = name.replace(/[^a-zA-Z0-9-_]+/g, '-').replace(/^-+|-+$/g, '');
  return sanitized.slice(0, 50) || 'export';
}

function formatEvidenceList(items: string[]): string {
  return items.join(' | ');
}

function formatEvidenceChunks(
  chunks: Array<{ text: string; source: string; url?: string | null }>,
): string {
  return chunks
    .map((chunk) => {
      const parts = [chunk.text];
      if (chunk.source) parts.push(`[${chunk.source}]`);
      if (chunk.url) parts.push(chunk.url);
      return parts.join(' ');
    })
    .join(' | ');
}

const FACT_CHECK_HEADERS = [
  'Sentence #',
  'Claim',
  'Status',
  'Verdict',
  'Confidence',
  'Reasoning',
  'Sources Used',
  'Supporting Evidence',
  'Contradicting Evidence',
  'Error',
];

export function buildFactCheckResultsCsv(claims: ClaimCard[]): string {
  const rows = claims.map((claim) => [
    claim.sentence_position + 1,
    claim.text,
    claim.validationStatus,
    claim.verdict ?? '',
    claim.confidence ?? '',
    claim.reasoning ?? '',
    claim.source_types_used.join('; '),
    formatEvidenceChunks(claim.supporting_chunks),
    formatEvidenceChunks(claim.contradicting_chunks),
    claim.error ?? '',
  ]);
  return rowsToCsv(FACT_CHECK_HEADERS, rows);
}

export function downloadFactCheckResultsCsv(claims: ClaimCard[]): void {
  const csv = buildFactCheckResultsCsv(claims);
  downloadTextFile(`fact-check-results-${timestampForFilename()}.csv`, csv);
}

const CHECKLIST_HEADERS = [
  'Rule Title',
  'Rule ID',
  'Verdict',
  'Condition Met',
  'Reasoning',
  'Evidence',
  'Criteria',
  'Condition',
  'Section Match',
  'Section Match Type',
];

function checklistResultRow(result: ReviewResultOut): CsvCell[] {
  return [
    result.rule_title,
    result.rule_id,
    result.verdict,
    result.condition_met ?? '',
    result.reasoning ?? '',
    formatEvidenceList(result.evidence),
    result.rule_criteria,
    result.rule_condition ?? '',
    result.section_match ?? '',
    result.section_match_type ?? '',
  ];
}

export function buildChecklistResultsCsv(results: ReviewResultOut[]): string {
  const rows = [...results]
    .sort((a, b) =>
      a.rule_title.localeCompare(b.rule_title, undefined, { numeric: true }),
    )
    .map(checklistResultRow);
  return rowsToCsv(CHECKLIST_HEADERS, rows);
}

export function downloadChecklistResultsCsv(
  results: ReviewResultOut[],
  checklistName?: string | null,
): void {
  const csv = buildChecklistResultsCsv(results);
  const prefix = checklistName
    ? `${sanitizeFilenameSegment(checklistName)}-checklist-results`
    : 'checklist-results';
  downloadTextFile(`${prefix}-${timestampForFilename()}.csv`, csv);
}
